<template>
  <div class="space-y-4">
    <!-- Chain Selector -->
    <section class="rounded-2xl border border-gray-200 p-4 space-y-1.5" :class="cardClass">
      <label class="text-xs font-medium text-[#4e7397] uppercase tracking-wide">Network</label>
      <ChainSelector
        :chains="sourceChainOptions"
        :selected-chain-id="selectedChainId"
        :disabled="routeEditLocked"
        @select="selectChain"
      />
    </section>

    <!-- Wallet Connection -->
    <WalletConnectionPanel
      :source-chain-type="sourceChainType"
      :source-wallet-address="sourceWalletAddress"
      :ton-address="state.ton.address"
      :needs-ton-reconnect="needsTonReconnect"
      :is-source-busy="isSourceBusy"
      :is-ton-busy="isTonBusy"
      :disconnect-btn-border-class="disconnectBtnBorderClass"
      :disconnect-btn-class="disconnectBtnClass"
      :phantom-deeplink-available="phantomDeeplinkAvailable"
      @connect-source="handleConnectSource"
      @disconnect-source="handleDisconnectSource"
      @connect-ton="handleConnectTon"
      @disconnect-ton="handleDisconnectTon"
    />

    <!-- Token Selection & Amount -->
    <TokenAmountForm
      :card-class="cardClass"
      :input-bg-class="inputBgClass"
      :prompt-padding-class="promptPaddingClass"
      :source-chain-type="sourceChainType"
      :source-wallet-address="sourceWalletAddress"
      :available-source-tokens="availableSourceTokens"
      :selected-source-token-address="selectedSourceTokenAddress"
      :selected-chain-id="selectedChainId"
      :is-loading-tokens="isLoadingTokens"
      :token-load-blocked-message="tokenLoadBlockedMessage"
      :tokens-with-balance-count="tokensWithBalanceCount"
      :selected-token="selectedToken"
      :source-amount="sourceAmount"
      :source-amount-usd="sourceAmountUsd"
      :balance-usd="balanceUsd"
      :spendable-balance-label="spendableBalanceLabel"
      :quick-amount-options="quickAmountOptions"
      :amount-validation-error="amountValidationError"
      :recovery-hint="recoveryHint"
      :route-locked="routeEditLocked"
      @select-token="selectToken"
      @amount-input="handleAmountInput"
      @set-max="setMaxAmount"
      @quick-amount="applyQuickAmount"
    />

    <!-- Route Stepper -->
    <RouteStepper
      v-if="
        state.steps.length > 0 ||
        state.quote ||
        state.status === 'quote_loading' ||
        routeUxStatus === 'invalidated'
      "
      :state="state"
      :source-amount="sourceAmount"
      :route-ux-status="routeUxStatus"
    />

    <!-- Solana Bridge Provider Selector -->
    <section
      v-if="showSolanaProviderSelector"
      class="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-3"
    >
      <div class="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p class="text-xs text-[#4e7397]">Bridge Provider</p>
        </div>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            :class="
              solanaBridgeProvider === 'layerswap'
                ? 'border-[#1980e6] bg-[#1980e6]/10 text-[#1980e6]'
                : 'border-gray-300 text-[#4e7397] hover:border-[#1980e6]/50 hover:text-[#1980e6]'
            "
            :disabled="routeLocked"
            @click="switchSolanaBridgeProvider('layerswap')"
          >
            Layerswap
          </button>
          <button
            type="button"
            class="px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            :class="
              solanaBridgeProvider === 'rango'
                ? 'border-[#1980e6] bg-[#1980e6]/10 text-[#1980e6]'
                : 'border-gray-300 text-[#4e7397] hover:border-[#1980e6]/50 hover:text-[#1980e6]'
            "
            :disabled="routeLocked"
            @click="switchSolanaBridgeProvider('rango')"
          >
            Rango
          </button>
        </div>
      </div>
    </section>

    <!-- Swap History: mobile inline -->
    <div class="md:hidden">
      <RecentSwapsInline :wallet-address="state.ton.address" />
    </div>

    <!-- Error & Wallet-Assist Banners -->
    <StatusBanner
      :error-message="friendlyErrorMessage || uiError"
      :wallet-assist-visible="walletAssistVisible"
      :wallet-assist-message="walletAssistMessage"
      :wallet-assist-name="walletAssistName"
      :has-resume-url="!!state.walletAssist?.resumeUrl"
      @open-wallet-assist="openWalletAssist"
    />

    <!-- Zombie TON state hint: bridge finished, TonConnect session is gone -->
    <div
      v-if="needsTonReconnect"
      class="rounded-2xl border border-[#1980e6]/30 bg-[#1980e6]/5 p-4 space-y-1"
    >
      <p class="text-sm font-semibold text-[#0e141b]">Almost done — reconnect your TON wallet</p>
      <p class="text-xs text-[#4e7397]">
        The bridge delivered USDT to
        <span class="font-mono">{{ maskedTonAddress }}</span
        >. Reconnect that wallet to finish the STON.fi swap into SPOTTY.
      </p>
    </div>

    <div
      v-else-if="needsTonBackendReauth"
      class="rounded-2xl border border-[#1980e6]/30 bg-[#1980e6]/5 p-4 space-y-1"
    >
      <p class="text-sm font-semibold text-[#0e141b]">TON wallet connected</p>
      <p class="text-xs text-[#4e7397]">
        Re-authenticate the backend session before continuing. Your TonConnect signing session will
        stay available until you start the fresh proof flow.
      </p>
    </div>

    <!-- Action Button (only rendered if parent doesn't handle it externally) -->
    <template v-if="!externalActionButton">
      <button
        type="button"
        class="w-full px-6 py-4 rounded-xl bg-[#1980e6] text-white font-semibold text-lg hover:bg-[#166ec5] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 overflow-hidden"
        :disabled="isContinueDisabled || isFlowBusy"
        @click="handleContinue"
      >
        <svg
          v-if="isFlowBusy"
          class="w-5 h-5 animate-spin flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <span class="truncate">{{ continueLabel }}</span>
      </button>
      <button
        v-if="canCancelExecution"
        type="button"
        class="w-full mt-2 px-6 py-3 rounded-xl border border-[#b42318]/30 bg-white text-[#b42318] font-medium hover:bg-[#b42318]/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        @click="handleCancel()"
      >
        {{ state.isCancellingExecution ? 'Cancelling…' : 'Cancel' }}
      </button>
    </template>
    <button
      v-if="showPhantomBridgeFallback"
      type="button"
      class="w-full min-h-12 px-6 py-3 rounded-xl border border-[#1980e6]/30 bg-white text-[#1980e6] font-medium hover:bg-[#1980e6]/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      :disabled="isFlowBusy"
      @click="handlePhantomBridgeFallback"
    >
      Open Phantom app
    </button>

    <!-- Developer diagnostics -->
    <div
      v-if="debugLog.length"
      class="mt-4 p-3 rounded-xl bg-gray-900 text-green-400 text-xs font-mono max-h-48 overflow-y-auto"
    >
      <div class="flex justify-between items-center mb-1">
        <span class="text-gray-500"
          >Debug Log {{ debugCopyStatus ? `(${debugCopyStatus})` : '' }}</span
        >
        <div class="flex gap-2">
          <button class="text-gray-500 hover:text-white text-xs" @click="copyDebugLog">copy</button>
          <button class="text-gray-500 hover:text-white text-xs" @click="debugLog = []">
            clear
          </button>
        </div>
      </div>
      <div v-for="(line, i) in debugLog" :key="i" class="whitespace-pre-wrap break-all">
        {{ line }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ChainSelector, TokenAmountForm } from '../../source-tokens'
import RouteStepper from './RouteStepper.vue'
import { RecentSwapsInline } from '../../recent-swaps'
import { useBuySpottyContentPresenter } from './composables/useBuySpottyContentPresenter'
import StatusBanner from './sections/StatusBanner.vue'
import WalletConnectionPanel from '../../wallets/WalletConnectionPanel.vue'

const props = withDefaults(
  defineProps<{ variant?: 'page' | 'modal'; externalActionButton?: boolean }>(),
  { variant: 'page', externalActionButton: false }
)

const {
  amountValidationError,
  applyQuickAmount,
  availableSourceTokens,
  balanceUsd,
  canCancelExecution,
  routeLocked,
  routeEditLocked,
  cardClass,
  continueLabel,
  copyDebugLog,
  debugCopyStatus,
  debugLog,
  debouncedRequote,
  disconnectBtnBorderClass,
  disconnectBtnClass,
  friendlyErrorMessage,
  handleAmountInput,
  handleCancel,
  handleConnectSource,
  handleConnectTon,
  handleContinue,
  handlePhantomBridgeFallback,
  handleDisconnectSource,
  handleDisconnectTon,
  inputBgClass,
  isContinueDisabled,
  isFlowBusy,
  isLoadingTokens,
  isSourceBusy,
  isTonBusy,
  maskedTonAddress,
  needsTonBackendReauth,
  needsTonReconnect,
  openWalletAssist,
  phantomDeeplinkAvailable,
  promptPaddingClass,
  quickAmountOptions,
  recoveryHint,
  routeUxStatus,
  selectChain,
  selectedChainId,
  selectedSourceTokenAddress,
  selectedToken,
  selectToken,
  setMaxAmount,
  showSolanaProviderSelector,
  showPhantomBridgeFallback,
  solanaBridgeProvider,
  sourceAmount,
  sourceAmountUsd,
  sourceChainOptions,
  sourceChainType,
  sourceWalletAddress,
  spendableBalanceLabel,
  state,
  switchSolanaBridgeProvider,
  tokenLoadBlockedMessage,
  tokensWithBalanceCount,
  uiError,
  walletAssistMessage,
  walletAssistName,
  walletAssistVisible,
} = useBuySpottyContentPresenter(props)

defineExpose({
  continueLabel,
  isContinueDisabled,
  isFlowBusy,
  handleContinue,
  sourceWalletAddress,
  solanaBridgeProvider,
  sourceChainType,
  state,
  isCancellingExecution: computed(() => state.value.isCancellingExecution),
  uiError,
  debouncedRequote,
  switchSolanaBridgeProvider,
  canCancelExecution,
  handleCancel,
})
</script>
